import { useEffect, useRef, useState } from "react";

/* Reveals every .reveal element once it is near the viewport.
 * Uses plain scroll checks plus a short polling failsafe, so content can never stay invisible. */
export function useRevealOnScroll() {
  useEffect(() => {
    const check = () => {
      const vh = window.innerHeight;
      const pending = document.querySelectorAll(".reveal:not(.in)");
      pending.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < vh * 0.94 && r.bottom > -40) el.classList.add("in");
      });
      return pending.length;
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    document.addEventListener("visibilitychange", check);
    const poll = setInterval(() => {
      if (check() === 0) clearInterval(poll);
    }, 400);
    return () => {
      clearInterval(poll);
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, []);
}

/* Animates a number from 0 to `end` once the element is visible */
export function useCountUp(end, duration = 1600) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);
        setValue(Math.round(end * (1 - Math.pow(1 - t, 3))));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  return [ref, value];
}

/* 3D tilt toward the cursor for any element with the .tilt class */
export function useTilt() {
  useEffect(() => {
    if (window.matchMedia("(hover: none)").matches) return;
    const els = document.querySelectorAll(".tilt");
    const handlers = [];
    els.forEach((el) => {
      const move = (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform = `perspective(900px) rotateY(${x * 14}deg) rotateX(${-y * 14}deg) translateZ(14px)`;
      };
      const leave = () => {
        el.style.transform = "";
      };
      el.addEventListener("mousemove", move);
      el.addEventListener("mouseleave", leave);
      handlers.push([el, move, leave]);
    });
    return () => handlers.forEach(([el, m, l]) => {
      el.removeEventListener("mousemove", m);
      el.removeEventListener("mouseleave", l);
    });
  }, []);
}
